import { useState } from 'react';
import { useGameStore } from '../store';
import { MOCK_NAMES } from '../constants';
import { fmt } from '../utils/format';
import type { RankTab } from '../types';

// ── Mock data ────────────────────────────────────────────────────────────
function seeded(seed: number) {
  let s = seed;
  return () => { s = (s * 1664525 + 1013904223) & 0xffffffff; return (s >>> 0) / 0xffffffff; };
}

interface RankEntry {
  rank: number;
  name: string;
  val: number;
}

function makeMockList(seed: number, max: number, isInt: boolean, count = 50): RankEntry[] {
  const rng = seeded(seed);
  return Array.from({ length: count }, (_, i) => ({
    rank: i + 1,
    name: MOCK_NAMES[i % MOCK_NAMES.length],
    val: isInt
      ? Math.floor(rng() * max)
      : Math.floor(rng() * max) / 100,
  }));
}

const WEEKLY_CASH = makeMockList(1, 500_000_000_000, true);
const ALL_RET     = makeMockList(2, 50000, false);   // % * 100

// ── Hall-of-fame cards ───────────────────────────────────────────────────
function HofCard({ label, val, unit = '' }: { label: string; val: string; unit?: string }) {
  return (
    <div className="rank-card">
      <div className="lbl">{label}</div>
      <div className="val">{val}{unit}</div>
    </div>
  );
}

// ── Row ──────────────────────────────────────────────────────────────────
function RankRowEl({ e, isMe, format }: { e: RankEntry; isMe: boolean; format: (v: number) => string }) {
  const numStr = e.rank === 1 ? '🥇' : e.rank === 2 ? '🥈' : e.rank === 3 ? '🥉' : String(e.rank);
  const cls = ['rank-row', isMe ? 'me' : '', e.rank <= 3 ? `top${e.rank}` : ''].filter(Boolean).join(' ');
  return (
    <div className={cls}>
      <span className="num">{numStr}</span>
      <span className="name">{e.name}{isMe ? ' (나)' : ''}</span>
      <span className="val">{format(e.val)}</span>
    </div>
  );
}

// ── RankingScreen ────────────────────────────────────────────────────────
export default function RankingScreen() {
  const show   = useGameStore((s) => s.showRankScreen);
  const setShow = useGameStore((s) => s.setShow);
  const stats  = useGameStore((s) => s.stats);
  const cash   = useGameStore((s) => s.cash);
  const [tab, setTab] = useState<RankTab>('weekly');

  if (!show) return null;

  // My rank (mock: always 42nd for now)
  const myRank = 42;

  const myWeeklyEntry: RankEntry = { rank: myRank, name: '나', val: cash };
  const myRetEntry: RankEntry    = { rank: myRank, name: '나', val: stats.maxRet };

  return (
    <div id="rank-screen">
      <div className="rank-header">
        <button className="rank-back" onClick={() => setShow('showRankScreen', false)}>←</button>
        <span className="rank-title">🏆 명예의 전당</span>
      </div>

      <div className="rank-tabs">
        {(['weekly', 'all', 'me'] as RankTab[]).map((t) => (
          <div
            key={t}
            className={`rank-tab${tab === t ? ' active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'weekly' ? '주간' : t === 'all' ? '전체' : '내 기록'}
          </div>
        ))}
      </div>

      <div className="rank-body">
        {tab === 'weekly' && (
          <>
            {WEEKLY_CASH.map((e) => (
              <RankRowEl key={e.rank} e={e} isMe={e.rank === myRank} format={(v) => fmt(v) + '냥'} />
            ))}
            <RankRowEl e={myWeeklyEntry} isMe format={(v) => fmt(v) + '냥'} />
          </>
        )}

        {tab === 'all' && (
          <div className="rank-section">
            <HofCard label="💰 최고 재산" val={fmt(stats.maxCash)} unit="냥" />
            <HofCard label="🚀 최고 수익률 (한 방)" val={stats.maxRet.toFixed(2)} unit="%" />
            <HofCard label="🔥 최고 연승" val={String(stats.maxStreak)} unit="연승" />
            <HofCard label="☠️ 청산 훈장" val={String(stats.liquidations)} unit="회" />
            <div style={{ height: 16 }} />
            {ALL_RET.map((e) => (
              <RankRowEl key={e.rank} e={e} isMe={e.rank === myRank} format={(v) => v.toFixed(2) + '%'} />
            ))}
            <RankRowEl e={myRetEntry} isMe format={(v) => v.toFixed(2) + '%'} />
          </div>
        )}

        {tab === 'me' && (
          <div className="rank-section">
            <HofCard label="💰 최고 재산" val={fmt(stats.maxCash)} unit="냥" />
            <HofCard label="🚀 최고 수익률 (한 방)" val={stats.maxRet.toFixed(2)} unit="%" />
            <HofCard label="🔥 최고 연승" val={String(stats.maxStreak)} unit="연승" />
            <HofCard label="☠️ 청산 훈장" val={String(stats.liquidations)} unit="회" />
            <HofCard label="💱 총 거래" val={String(stats.totalTrades)} unit="회" />
          </div>
        )}
      </div>

      {/* Sticky my-rank row for weekly/all tabs */}
      {(tab === 'weekly' || tab === 'all') && (
        <div className="rank-my-sticky">
          <RankRowEl
            e={tab === 'weekly' ? myWeeklyEntry : myRetEntry}
            isMe
            format={tab === 'weekly' ? (v) => fmt(v) + '냥' : (v) => v.toFixed(2) + '%'}
          />
        </div>
      )}
    </div>
  );
}
